import { Compass, Video, Crown, ArrowRight, ArrowUpRight, MessageCircle, CalendarCheck } from "lucide-react";
import { Reveal, scrollToSection, trackEvent } from "./Reveal";
import { PAYMENT_LINKS } from "@/config/site";

const BOUSSOLE_URL = "https://boussole.yassineabou.fr";

const STEPS = [
  { icon: MessageCircle, title: "1. Diagnostic offert", desc: "Un premier échange pour comprendre la situation et définir le cap." },
  { icon: Compass, title: "2. Travail à son rythme", desc: "L'élève avance dans l'application Boussole, quand il veut, où il veut." },
  { icon: CalendarCheck, title: "3. Point tous les 15 jours", desc: "Coaching en visio pour garder le cap, ajuster et échanger avec l'apprenant." },
];

const TIERS = [
  {
    tier: "autonomie",
    annualPrice: "178,80 €",
    saving: "14,90 €",
    name: "Boussole Autonomie",
    price: "14,90 €",
    badge: "Offre de lancement",
    highlight: false,
    commitment: "Engagement 12 mois",
    features: [
      "Accès complet à l'application Boussole",
      "Programme structuré pas à pas",
      "Progression et bilans automatisés",
      "Travail à son rythme, sans séance individuelle",
    ],
    testid: "pricing-card-autonomie",
  },
  {
    tier: "collectif",
    annualPrice: "358,80 €",
    saving: "29,90 €",
    name: "Cercle Collectif",
    price: "29,90 €",
    badge: null,
    highlight: false,
    commitment: "Engagement 12 mois",
    features: [
      "Tout Boussole Autonomie",
      "Rendez-vous collectif en visio tous les 15 jours",
      "Séance animée par Yassine",
      "Échanges et questions en direct",
    ],
    testid: "pricing-card-collectif",
  },
  {
    tier: "individuel",
    annualPrice: "1 068 €",
    saving: "89 €",
    name: "Suivi Individuel",
    price: "89 €",
    badge: "Le plus personnalisé",
    highlight: true,
    commitment: "Engagement 12 mois",
    features: [
      "Tout Boussole Autonomie",
      "Coaching individuel tous les 15 jours",
      "Un cours de sport par mois, en visio ou en présentiel : reprise d'activité ou développement physique",
      "Séance de questions-réponses hebdomadaire, sur demande, pour garder un cap clair",
      "Bilan de progression écrit tous les 3 mois",
      "Une séance parents par trimestre pour soutenir l'apprenant",
      "Programme physique personnalisé en PDF, pour s'entraîner entre les séances",
      "Séance de préparation avant une échéance importante (examen, oral, compétition), sur demande",
      "Séances de sport supplémentaires à la demande, facturées en supplément, avec packs à tarif dégressif",
    ],
    testid: "pricing-card-individuel",
  },
];

const Accompagnement = () => {
  return (
    <section id="accompagnement" className="relative py-24 sm:py-32 bg-coal border-y border-white/5 overflow-hidden" data-testid="accompagnement-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-ember">L'accompagnement</p>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/12 border border-gold/30 px-3.5 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-300">
              <Crown size={12} /> Premium
            </span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-slate-100 max-w-3xl leading-tight">
            Autonomie au quotidien, cap humain tous les 15 jours
          </h2>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
            Un dispositif complet qui combine le travail à son rythme sur une application dédiée
            et un vrai suivi humain en distanciel — pour que le cap ne soit jamais perdu de vue.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-5 md:grid-cols-2">
          <Reveal>
            <div
              data-testid="offer-card-boussole"
              className="relative h-full rounded-2xl border border-gold/25 bg-obsidian/70 p-7 sm:p-9 transition-all duration-300 hover:border-gold/50 hover:-translate-y-1.5 flex flex-col"
            >
              <span className="absolute top-6 right-6 inline-flex items-center gap-1.5 rounded-full bg-gold px-3.5 py-1 text-[11px] font-black uppercase tracking-wide text-obsidian">
                <span className="w-1.5 h-1.5 rounded-full bg-obsidian animate-pulse" />
                Disponible en ligne
              </span>
              <span className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gold/12 border border-gold/30">
                <Compass size={26} className="text-gold" />
              </span>
              <h3 className="mt-6 font-heading text-2xl font-extrabold uppercase text-slate-100">
                L'application « Boussole »
              </h3>
              <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed">
                Un coach mental de poche, pensé pour les apprenants : chacun avance
                <strong className="text-gold"> à son rythme</strong>, guidé pas à pas par un
                programme structuré et bienveillant.
              </p>
              <ul className="mt-5 space-y-2.5 text-sm text-slate-300 flex-1">
                <li className="flex items-center gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" /> Programme structuré : objectifs, routines, gestion des pensées</li>
                <li className="flex items-center gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" /> Exercices guidés et concrets, applicables dès le premier jour</li>
                <li className="flex items-center gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" /> Progression visible et bilans automatisés</li>
                <li className="flex items-center gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" /> Accessible partout, quand il veut, où il veut</li>
              </ul>
              <a
                href={BOUSSOLE_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="boussole-discover-cta"
                className="group mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-gold text-obsidian hover:bg-amber-400 px-6 py-3.5 text-xs font-bold uppercase tracking-wide transition-colors duration-200"
              >
                Découvrir Boussole
                <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                Accessible dès maintenant, sur ordinateur, tablette et mobile.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div
              data-testid="offer-card-coaching"
              className="h-full rounded-2xl border border-white/10 bg-obsidian/70 p-7 sm:p-9 transition-all duration-300 hover:border-ember/40 hover:-translate-y-1.5"
            >
              <span className="flex items-center justify-center w-14 h-14 rounded-2xl bg-ember/12 border border-ember/30">
                <Video size={26} className="text-ember" />
              </span>
              <h3 className="mt-6 font-heading text-2xl font-extrabold uppercase text-slate-100">
                Coaching bimensuel en visio
              </h3>
              <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed">
                Tous les 15 jours, un échange en distanciel avec Yassine : point sur la
                progression, ajustement des objectifs et temps d'écoute directe
                avec l'apprenant. L'application avance, l'humain garde le cap.
              </p>
              <ul className="mt-5 space-y-2.5 text-sm text-slate-400">
                <li className="flex items-center gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-ember" /> Visio ou téléphone, selon votre préférence</li>
                <li className="flex items-center gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-ember" /> Compte rendu et prochaines étapes après chaque séance</li>
                <li className="flex items-center gap-2.5"><span className="w-1.5 h-1.5 rounded-full bg-ember" /> Disponibilité entre les séances pour les questions urgentes</li>
              </ul>
            </div>
          </Reveal>
        </div>

        <div id="tarifs" className="mt-20 scroll-mt-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <Crown size={18} className="text-gold" />
              <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-gold">Formules & tarifs</p>
            </div>
            <h3 className="font-heading text-2xl sm:text-3xl font-extrabold uppercase text-slate-100">
              Choisissez le niveau d'accompagnement
            </h3>
          </Reveal>

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {TIERS.map((tier, i) => (
              <Reveal key={tier.testid} delay={i * 0.1}>
                <div
                  data-testid={tier.testid}
                  className={`relative h-full rounded-2xl border p-7 flex flex-col transition-all duration-300 hover:-translate-y-1.5 ${
                    tier.highlight
                      ? "border-gold/50 bg-obsidian shadow-[0_20px_60px_-20px_rgba(255,183,3,0.25)]"
                      : "border-white/10 bg-obsidian/60 hover:border-white/25"
                  }`}
                >
                  {tier.badge && (
                    <span className={`absolute -top-3 left-6 rounded-full px-3.5 py-1 text-[10px] font-black uppercase tracking-wide ${
                      tier.highlight ? "bg-gold text-obsidian" : "bg-ember text-white"
                    }`}>
                      {tier.badge}
                    </span>
                  )}
                  <h4 className="font-heading text-xl font-bold uppercase text-slate-100">{tier.name}</h4>
                  <p className="mt-4">
                    <span className="font-heading text-4xl sm:text-5xl font-black text-slate-100">{tier.price}</span>
                    <span className="text-sm text-slate-400"> /4 semaines</span>
                  </p>
                  {tier.commitment && (
                    <p className="mt-1.5 text-xs font-semibold text-amber-300/90">{tier.commitment}</p>
                  )}
                  <ul className="mt-6 space-y-3 flex-1">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300 leading-relaxed">
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${tier.highlight ? "bg-gold" : "bg-ember"}`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={PAYMENT_LINKS[tier.tier].weeks4}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`${tier.testid}-cta`}
                    onClick={() => trackEvent("subscribe_click", { tier: tier.tier, period: "4_weeks" })}
                    className={`mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-xs font-bold uppercase tracking-wide transition-colors duration-200 ${
                      tier.highlight
                        ? "bg-gold text-obsidian hover:bg-amber-400"
                        : "border border-white/20 text-slate-100 hover:border-ember hover:text-white"
                    }`}
                  >
                    S'abonner · {tier.price} / 4 semaines
                  </a>
                  <a
                    href={PAYMENT_LINKS[tier.tier].annual}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`${tier.testid}-cta-annual`}
                    onClick={() => trackEvent("subscribe_click", { tier: tier.tier, period: "annual" })}
                    className="mt-3 w-full rounded-2xl border border-gold/40 px-4 py-3 text-center text-xs font-bold uppercase tracking-wide text-gold hover:bg-gold/10 transition-colors duration-200"
                  >
                    Payer l'année : {tier.annualPrice}
                    <span className="block mt-1 text-[11px] font-semibold normal-case tracking-normal text-amber-300/90">
                      4 semaines offertes, soit {tier.saving} d'économie
                    </span>
                  </a>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <p className="mt-7 text-xs text-slate-500 leading-relaxed max-w-3xl">
              Paiement sécurisé par Stripe — prélèvement automatique toutes les 4 semaines, ou paiement annuel en une fois (52 semaines pour le prix de 48), reçu par email.
              En vous abonnant, vous acceptez nos{" "}
              <a href="/cgv" data-testid="pricing-cgv-link" className="underline underline-offset-2 hover:text-slate-300 transition-colors">
                Conditions Générales de Vente
              </a>{" "}
              (engagement de 12 mois pour toutes les formules).
              Tarifs de lancement, susceptibles d'évoluer.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-8 sm:grid-cols-3">
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <div data-testid={`accompagnement-step-${i}`} className="flex sm:flex-col items-start gap-4">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-ember/12 border border-ember/30 shrink-0">
                  <s.icon size={18} className="text-ember" />
                </span>
                <div>
                  <h4 className="font-heading text-lg font-bold uppercase text-slate-100">{s.title}</h4>
                  <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <button
            data-testid="accompagnement-cta"
            onClick={(e) => { e.preventDefault(); scrollToSection("#contact"); }}
            className="group mt-12 inline-flex items-center gap-2 rounded-full bg-ember hover:bg-emberdark px-7 py-4 text-sm font-bold uppercase tracking-wide transition-colors duration-200"
          >
            Commencer par le diagnostic offert
            <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};

export default Accompagnement;
